import { create } from 'zustand'
import { ModelConfig } from '../types'
import { genId, nowIso } from '../utils/format'

interface TestResult {
  success: boolean
  latencyMs: number
  message: string
}

interface ModelStore {
  models: ModelConfig[]
  testing: Record<string, boolean>
  testResults: Record<string, TestResult>
  addModel: (
    m: Omit<
      ModelConfig,
      | 'modelId'
      | 'apiKeyMasked'
      | 'isDefault'
      | 'isClaudeCodeDefault'
      | 'createdAt'
      | 'updatedAt'
    > & { apiKey: string },
  ) => void
  updateModel: (id: string, patch: Partial<ModelConfig>) => void
  deleteModel: (id: string) => void
  setDefault: (id: string) => void
  setClaudeCodeDefault: (id: string) => void
  testConnection: (id: string) => Promise<void>
}

function maskKey(key: string): string {
  if (!key) return '****'
  const tail = key.slice(-4)
  return `${key.slice(0, 6)}****${tail}`
}

const seed: ModelConfig[] = [
  {
    modelId: 'model_claude_sonnet',
    name: 'Claude Sonnet 4',
    provider: 'anthropic',
    sdkType: 'claude_code_sdk',
    modelName: 'claude-sonnet-4-20250514',
    apiKeyMasked: 'sk-ant-****1234',
    isDefault: true,
    isClaudeCodeDefault: true,
    enabled: true,
    capabilities: ['tool_use', 'long_context', 'vision'],
    defaultParams: { temperature: 0.2, maxTokens: 8192, topP: 1 },
    createdAt: '2026-06-01T12:00:00Z',
    updatedAt: '2026-06-04T09:30:00Z',
  },
  {
    modelId: 'model_gpt4o',
    name: 'GPT-4o',
    provider: 'openai_compatible',
    sdkType: 'openai_compatible',
    modelName: 'gpt-4o',
    baseUrl: 'https://api.openai.com/v1',
    apiKeyMasked: 'sk-proj****9f2a',
    isDefault: false,
    isClaudeCodeDefault: false,
    enabled: true,
    capabilities: ['tool_use', 'vision', 'json_mode'],
    defaultParams: { temperature: 0.3, maxTokens: 4096 },
    createdAt: '2026-06-02T08:00:00Z',
    updatedAt: '2026-06-03T18:20:00Z',
  },
  {
    modelId: 'model_qwen',
    name: 'Qwen2.5 72B',
    provider: 'custom',
    sdkType: 'generic_llm',
    modelName: 'qwen2.5-72b-instruct',
    baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    apiKeyMasked: 'sk-qwen****77ee',
    isDefault: false,
    isClaudeCodeDefault: false,
    enabled: false,
    capabilities: ['tool_use', 'long_context', 'json_mode'],
    defaultParams: { temperature: 0.5, maxTokens: 8192, topP: 0.9 },
    createdAt: '2026-05-28T11:00:00Z',
    updatedAt: '2026-05-30T15:45:00Z',
  },
]

export const useModelStore = create<ModelStore>((set, get) => ({
  models: seed,
  testing: {},
  testResults: {},
  addModel: (m) =>
    set((s) => ({
      models: [
        ...s.models,
        {
          modelId: genId('model'),
          name: m.name,
          provider: m.provider,
          sdkType: m.sdkType,
          modelName: m.modelName,
          baseUrl: m.baseUrl,
          apiKeyMasked: maskKey(m.apiKey),
          isDefault: false,
          isClaudeCodeDefault: false,
          enabled: m.enabled,
          capabilities: m.capabilities,
          defaultParams: m.defaultParams,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
      ],
    })),
  updateModel: (id, patch) =>
    set((s) => ({
      models: s.models.map((m) =>
        m.modelId === id ? { ...m, ...patch, updatedAt: nowIso() } : m,
      ),
    })),
  deleteModel: (id) =>
    set((s) => ({ models: s.models.filter((m) => m.modelId !== id) })),
  setDefault: (id) =>
    set((s) => ({
      models: s.models.map((m) => ({ ...m, isDefault: m.modelId === id })),
    })),
  setClaudeCodeDefault: (id) =>
    set((s) => ({
      models: s.models.map((m) => ({
        ...m,
        isClaudeCodeDefault: m.modelId === id,
      })),
    })),
  testConnection: async (id) => {
    set((s) => ({ testing: { ...s.testing, [id]: true } }))
    const latency = 400 + Math.floor(Math.random() * 1200)
    await new Promise((r) => setTimeout(r, Math.min(latency, 1600)))
    const success = Math.random() > 0.15
    set((s) => ({
      testing: { ...s.testing, [id]: false },
      testResults: {
        ...s.testResults,
        [id]: {
          success,
          latencyMs: latency,
          message: success ? '模型连接成功' : '连接失败：鉴权或网络错误',
        },
      },
      models: s.models.map((m) =>
        m.modelId === id ? { ...m, updatedAt: nowIso() } : m,
      ),
    }))
    if (!success) {
      // mark as connection failed visually via enabled stays, handled in UI by testResult
    }
  },
}))

export function modelUsageLabel(m: ModelConfig): string[] {
  const labels: string[] = []
  if (m.isDefault) labels.push('默认模型')
  if (m.isClaudeCodeDefault) labels.push('Claude Code 当前模型')
  return labels
}
